import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useRegistrationActions } from "../hooks/actions/useRegistrationActions";
import { useMenu, useAuth } from "../hooks/index.js";
import { handleLogin } from "./handleLogin.js";
import { handleResetPassword } from "./handleResetPassword.js";
import { handleSetNewPassword } from "./handleSetNewPassword.js";
import "../styles/SideMenu.css";

const LoginSideMenu = () => {
  const navigate = useNavigate();
  const { createRegularAccount } = useRegistrationActions();

  // Use Jotai hooks instead of props
  const { menuOpen, showSubscriptionMenu, setMenuOpen, setIsOverlayOpen } =
    useMenu();
  const {
    email,
    setEmail,
    password,
    setPassword,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    resetToken,
    error,
    setError,
    successMessage,
    setSuccessMessage,
    isRegistering,
    setIsRegistering,
    isRecovering,
    setIsRecovering,
    isSettingNewPassword,
    setIsSettingNewPassword,
    referrer,
  } = useAuth();
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaContainerRef = useRef(null);

  useEffect(() => {
    if (!captchaContainerRef.current || process.env.NODE_ENV !== "production")
      return;

    let widgetId;
    const sitekey = "a83c57e4-64f8-4d6e-beba-aa2c5f32d6b0";

    const renderWidget = () => {
      if (!captchaContainerRef.current) return;
      widgetId = window.hcaptcha.render(captchaContainerRef.current, {
        sitekey,
        size: "compact",
        callback: (token) => setCaptchaToken(token),
        "expired-callback": () => setCaptchaToken(null),
      });
    };

    if (window.hcaptcha) {
      renderWidget();
      return () => {
        if (window.hcaptcha && widgetId != null) window.hcaptcha.reset(widgetId);
      };
    }

    const interval = setInterval(() => {
      if (window.hcaptcha) {
        clearInterval(interval);
        renderWidget();
      }
    }, 100);

    return () => {
      clearInterval(interval);
      if (window.hcaptcha && widgetId != null) window.hcaptcha.reset(widgetId);
    };
  }, []);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!captchaToken && process.env.NODE_ENV === "production") {
      setError("Please complete the captcha verification.");
      return;
    }

    if (isRegistering) {
      createRegularAccount(e, {
        email,
        firstName,
        lastName,
        password,
        captchaToken,
        referrer,
        setError,
        setSuccessMessage,
        setIsRegistering,
        setIsOverlayOpen,
        setMenuOpen,
      });
    } else if (isRecovering) {
      handleResetPassword(e, {
        email,
        captchaToken,
        setError,
        setSuccessMessage,
      });
    } else if (isSettingNewPassword) {
      handleSetNewPassword(e, {
        password,
        resetToken,
        setError,
        setSuccessMessage,
        setPassword,
      });
      setIsSettingNewPassword(false);
    } else {
      handleLogin(e, {
        email,
        password,
        captchaToken,
        setError,
        setSuccessMessage,
        setMenuOpen,
        setIsOverlayOpen,
        setEmail,
        setPassword,
      });
    }

    // Reset captcha after submission
    if (window.hcaptcha && process.env.NODE_ENV === "production")
      window.hcaptcha.reset();
    setCaptchaToken(null);
  };

  return (
    <nav className={`side-menu ${menuOpen ? "open" : ""}`}>
      <div className="login-form">
        <form onSubmit={handleFormSubmit}>
          {!isSettingNewPassword && (
            <>
              <label>Email:</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </>
          )}
          {(!isRecovering || isSettingNewPassword) && (
            <>
              <label>{isSettingNewPassword ? "New " : ""}Password:</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </>
          )}

          {isRegistering && !isRecovering && (
            <>
              <label>First Name:</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
              <label>Last Name:</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </>
          )}

          {process.env.NODE_ENV === "production" && (
            <div ref={captchaContainerRef} className="hcaptcha-container"></div>
          )}

          <button type="submit">
            {isRegistering
              ? "Register"
              : isRecovering || isSettingNewPassword
              ? "Reset"
              : "Login"}
          </button>
        </form>

        {!isRegistering && !isRecovering && !isSettingNewPassword && (
          <>
            <button
              className="unregistered"
              onClick={() => {
                setIsRegistering(true);
                setSuccessMessage("");
                setError("");
              }}
            >
              No Account? Register For Free
            </button>

            <button
              className="unregistered"
              onClick={() => {
                setIsRecovering(true);
                setSuccessMessage("");
                setError("");
              }}
            >
              Reset Password
            </button>
          </>
        )}

        {isRegistering && (
          <button
            onClick={() => {
              setIsRegistering(false);
              setSuccessMessage("");
              setError("");
            }}
          >
            Cancel
          </button>
        )}

        {isRecovering && (
          <button
            onClick={() => {
              setIsRecovering(false);
              setSuccessMessage("");
              setError("");
            }}
          >
            Cancel
          </button>
        )}

        {isSettingNewPassword && (
          <button
            onClick={() => {
              setIsSettingNewPassword(false);
              setSuccessMessage("");
              setError("");
            }}
          >
            Cancel
          </button>
        )}

        {successMessage && <p className="success-message">{successMessage}</p>}

        {error && <p className="error-message">{error}</p>}
      </div>
    </nav>
  );
};

export default LoginSideMenu;
